import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReviewDetails } from "@screens/reviews/types";
import { getReviewById } from "services/MerchantHostApi";

interface ReviewState {
  reviews: ReviewDetails[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  lastFetchedAt: number | null;
  merchantId: string | null;
}

const initialState: ReviewState = {
  reviews: [],
  status: "idle",
  error: null,
  lastFetchedAt: null,
  merchantId: null,
};

/**
 * Async thunk to fetch reviews for a merchant
 * Implements basic caching to avoid unnecessary API calls
 * Cache expires after 5 minutes
 */
export const fetchReviews = createAsyncThunk(
  "reviews/fetchReviews",
  async (merchantId: string, { getState, rejectWithValue }) => {
    const state = getState() as { reviews: ReviewState };

    // Implement 12-hour cache
    const CACHE_DURATION = 12 * 60 * 60 * 1000;
    const now = Date.now();

    if (
      state.reviews.merchantId === merchantId &&
      state.reviews.lastFetchedAt &&
      now - state.reviews.lastFetchedAt < CACHE_DURATION &&
      state.reviews.reviews.length > 0
    ) {
      // Return cached data
      return {
        reviews: state.reviews.reviews,
        merchantId,
      };
    }

    try {
      const response = await getReviewById(merchantId);
      return {
        reviews: response || [],
        merchantId,
      };
    } catch (error) {
      console.error("Error while fetching reviews", error);
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch reviews"
      );
    }
  }
);

const reviewSlice = createSlice({
  name: "reviews",
  initialState,
  reducers: {
    /**
     * Clear all reviews and reset state
     * Use when logging out or changing merchants
     */
    clearReviews: (state) => {
      state.reviews = [];
      state.error = null;
      state.lastFetchedAt = null;
      state.merchantId = null;
      state.status = "idle";
    },

    /**
     * Force refresh reviews by invalidating cache
     */
    invalidateReviewCache: (state) => {
      state.lastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReviews.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(
        fetchReviews.fulfilled,
        (
          state,
          action: PayloadAction<{ reviews: ReviewDetails[]; merchantId: string }>
        ) => {
          state.status = "succeeded";
          state.reviews = action.payload.reviews;
          state.merchantId = action.payload.merchantId;
          state.lastFetchedAt = Date.now();
          state.error = null;
        }
      )
      .addCase(fetchReviews.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const { clearReviews, invalidateReviewCache } = reviewSlice.actions;
export default reviewSlice.reducer;
